import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import adminApiService from '../../services/adminApiService';
import toast from 'react-hot-toast';
import {
  BriefcaseIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  UsersIcon,
  CalendarIcon,
  FunnelIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  TrophyIcon,
  SparklesIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  PlusIcon,
  TagIcon,
  StarIcon
} from '@heroicons/react/24/outline';

interface Job {
  id: string;
  title: string;
  description: string;
  requirements: string[];
  skillsRequired: string[];
  skillWeights: Record<string, number>;
  employerId: string;
  employerName: string;
  companyDescription?: string;
  location: string;
  remoteType: 'onsite' | 'remote' | 'hybrid';
  jobType: 'full-time' | 'part-time' | 'contract' | 'internship';
  experienceLevel: 'entry' | 'junior' | 'mid' | 'senior' | 'lead';
  salaryRange?: {
    min: number;
    max: number;
    currency: string;
  };
  benefits?: string[];
  applicationDeadline?: string;
  startDate?: string;
  isActive: boolean;
  isFeatured: boolean;
  applicationsCount: number;
  viewsCount: number;
  tags: string[];
  category: string;
  industryCategory: string;
  competitionLevel: 'low' | 'medium' | 'high';
  averageApplicationScore?: number;
  createdAt: string;
  updatedAt: string;
  approvedAt?: string;
  moderatedBy?: string;
  status: 'draft' | 'active' | 'paused' | 'closed' | 'under_review';
  
  // Admin-specific fields for moderation
  flaggedReasons?: string[];
  moderationNotes?: string;
  qualityScore?: number;
  engagementScore?: number;
}

interface JobStats {
  totalJobs: number;
  activeJobs: number;
  underReview: number;
  totalApplications: number;
  averageApplicationsPerJob: number;
  totalViews: number;
  flaggedJobs: number;
}

interface ModerationAction {
  action: 'approve' | 'reject' | 'flag' | 'feature' | 'unfeature';
  reason?: string;
  notes?: string;
}

const JobManagement: React.FC = () => {
  const { adminUser, hasPermission } = useAdmin();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [stats, setStats] = useState<JobStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [showModerationModal, setShowModerationModal] = useState(false);
  const [moderationAction, setModerationAction] = useState<ModerationAction>({ action: 'approve' });
  const [sortBy, setSortBy] = useState<'newest' | 'applications' | 'views' | 'competition' | 'salary'>('newest');

  useEffect(() => {
    fetchJobs();
    fetchStats();
  }, [selectedStatus, selectedCategory, sortBy]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      if (selectedStatus !== 'all') params.append('status', selectedStatus);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (searchQuery) params.append('search', searchQuery);
      params.append('sortBy', sortBy);

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/admin/jobs?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch jobs');
      }

      const result = await response.json();
      
      if (result.success) {
        setJobs(result.data.jobs);
      } else {
        toast.error(result.message || 'Failed to fetch jobs');
      }
    } catch (error: any) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/admin/jobs/stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setStats(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching job stats:', error);
    }
  };

  const handleModerationAction = async () => {
    if (!selectedJob) return;

    try {
      const response = await fetch(`/api/admin/jobs/${selectedJob.id}/moderate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          action: moderationAction.action,
          reason: moderationAction.reason,
          notes: moderationAction.notes,
          moderatedBy: adminUser?.uid
        })
      });

      if (!response.ok) {
        throw new Error('Failed to perform moderation action');
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success(`Job ${moderationAction.action}d successfully`);
        setShowModerationModal(false);
        setSelectedJob(null);
        fetchJobs();
        fetchStats();
      } else {
        throw new Error(result.message || 'Moderation action failed');
      }
    } catch (error: any) {
      console.error('Error performing moderation action:', error);
      toast.error(error.message || 'Failed to perform moderation action');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'under_review':
        return 'bg-yellow-100 text-yellow-800';
      case 'paused':
        return 'bg-gray-100 text-gray-800';
      case 'closed':
        return 'bg-red-100 text-red-800';
      case 'draft':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCompetitionColor = (level: string) => {
    switch (level) {
      case 'low':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'high':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatSalary = (salaryRange?: { min: number; max: number; currency: string }) => {
    if (!salaryRange) return 'Not specified';
    
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: salaryRange.currency,
      maximumFractionDigits: 0
    });
    
    return `${formatter.format(salaryRange.min)} - ${formatter.format(salaryRange.max)}`;
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just posted';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks}w ago`;
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = !searchQuery || 
      job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.employerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.skillsRequired.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return matchesSearch;
  });

  if (!hasPermission('jobs', 'read')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h2 className="mt-2 text-lg font-medium text-gray-900">Access Denied</h2>
          <p className="mt-1 text-gray-500">You don't have permission to manage jobs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Job Management</h1>
            <p className="text-gray-600 mt-2">
              Moderate job postings, analyze performance, and manage employer compliance
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchJobs}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2"
            >
              <ArrowPathIcon className="h-5 w-5" />
              Refresh
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
            >
              <FunnelIcon className="h-5 w-5" />
              Filters
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <BriefcaseIcon className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Jobs</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalJobs}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active Jobs</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.activeJobs}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <ClockIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Under Review</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.underReview}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Flagged</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.flaggedJobs}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search jobs, employers, or skills..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div className="flex gap-4">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="under_review">Under Review</option>
                <option value="paused">Paused</option>
                <option value="closed">Closed</option>
                <option value="draft">Draft</option>
              </select>

              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="all">All Categories</option>
                <option value="technology">Technology</option>
                <option value="marketing">Marketing</option>
                <option value="finance">Finance</option>
                <option value="design">Design</option>
                <option value="sales">Sales</option>
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="newest">Newest First</option>
                <option value="applications">Most Applications</option>
                <option value="views">Most Views</option>
                <option value="competition">Competition Level</option>
                <option value="salary">Highest Salary</option>
              </select>
            </div>
          </div>
        </div>

        {/* Job List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Job Postings ({filteredJobs.length})
              </h2>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-12">
              <BriefcaseIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search criteria or filters.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Job Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Metrics
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredJobs.map((job) => (
                    <tr key={job.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                              <BriefcaseIcon className="h-6 w-6 text-indigo-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-medium text-gray-900">
                                {job.title}
                              </div>
                              {job.isFeatured && (
                                <StarIcon className="h-4 w-4 text-yellow-500" />
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              {job.location} • {job.remoteType} • {job.jobType}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCompetitionColor(job.competitionLevel)}`}>
                                {job.competitionLevel} competition
                              </span>
                              <span className="text-xs text-gray-500">
                                {getTimeAgo(job.createdAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{job.employerName}</div>
                        <div className="text-sm text-gray-500">{job.category}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center gap-1 mb-1">
                            <UsersIcon className="h-4 w-4 text-gray-400" />
                            {job.applicationsCount} applications
                          </div>
                          <div className="flex items-center gap-1 mb-1">
                            <EyeIcon className="h-4 w-4 text-gray-400" />
                            {job.viewsCount} views
                          </div>
                          <div className="flex items-center gap-1">
                            <CurrencyDollarIcon className="h-4 w-4 text-gray-400" />
                            {formatSalary(job.salaryRange)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                          {job.status.replace('_', ' ')}
                        </span>
                        {job.flaggedReasons && job.flaggedReasons.length > 0 && (
                          <div className="mt-1">
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-100 text-red-800">
                              <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                              Flagged
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedJob(job);
                              setShowJobDetails(true);
                            }}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="View Details"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          
                          {hasPermission('jobs', 'update') && (
                            <button
                              onClick={() => {
                                setSelectedJob(job);
                                setShowModerationModal(true);
                              }}
                              className="text-green-600 hover:text-green-900"
                              title="Moderate"
                            >
                              <ShieldCheckIcon className="h-4 w-4" />
                            </button>
                          )}

                          {hasPermission('jobs', 'delete') && (
                            <button
                              onClick={() => {
                                // Handle delete job
                                console.log('Delete job', job.id);
                              }}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Job Details Modal */}
      {showJobDetails && selectedJob && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Job Details</h3>
              <button
                onClick={() => setShowJobDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">{selectedJob.title}</h4>
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                  <div className="flex items-center gap-1">
                    <BuildingOfficeIcon className="h-4 w-4" />
                    {selectedJob.employerName}
                  </div>
                  <div className="flex items-center gap-1">
                    <MapPinIcon className="h-4 w-4" />
                    {selectedJob.location} • {selectedJob.remoteType}
                  </div>
                  <div className="flex items-center gap-1">
                    <CalendarIcon className="h-4 w-4" />
                    {getTimeAgo(selectedJob.createdAt)}
                  </div>
                </div>
              </div>

              <div>
                <h5 className="font-medium text-gray-900 mb-2">Description</h5>
                <p className="text-gray-600">{selectedJob.description}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">Required Skills</h5>
                  <div className="flex flex-wrap gap-2">
                    {selectedJob.skillsRequired.map(skill => (
                      <span key={skill} className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-md">
                        {skill} {selectedJob.skillWeights[skill] && `(${selectedJob.skillWeights[skill]}/10)`}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h5 className="font-medium text-gray-900 mb-2">Job Details</h5>
                  <div className="space-y-2 text-sm">
                    <div><strong>Type:</strong> {selectedJob.jobType}</div>
                    <div><strong>Experience:</strong> {selectedJob.experienceLevel}</div>
                    <div><strong>Category:</strong> {selectedJob.category}</div>
                    <div><strong>Competition:</strong> {selectedJob.competitionLevel}</div>
                  </div>
                </div>
              </div>

              {selectedJob.requirements.length > 0 && (
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">Requirements</h5>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    {selectedJob.requirements.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedJob.benefits && selectedJob.benefits.length > 0 && (
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">Benefits</h5>
                  <div className="flex flex-wrap gap-2">
                    {selectedJob.benefits.map(benefit => (
                      <span key={benefit} className="px-2 py-1 bg-green-100 text-green-800 text-sm rounded-md">
                        {benefit}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{selectedJob.applicationsCount}</div>
                  <div className="text-sm text-gray-500">Applications</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{selectedJob.viewsCount}</div>
                  <div className="text-sm text-gray-500">Views</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">
                    {selectedJob.averageApplicationScore?.toFixed(1) || 'N/A'}
                  </div>
                  <div className="text-sm text-gray-500">Avg Score</div>
                </div>
              </div>

              {selectedJob.flaggedReasons && selectedJob.flaggedReasons.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <h5 className="font-medium text-red-900 mb-2">Flagged Issues</h5>
                  <ul className="list-disc list-inside space-y-1 text-red-700">
                    {selectedJob.flaggedReasons.map((reason, index) => (
                      <li key={index}>{reason}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Moderation Modal */}
      {showModerationModal && selectedJob && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Moderate Job: {selectedJob.title}</h3>
              <button
                onClick={() => setShowModerationModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Moderation Action
                </label>
                <select
                  value={moderationAction.action}
                  onChange={(e) => setModerationAction(prev => ({ ...prev, action: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="approve">Approve Job</option>
                  <option value="reject">Reject Job</option>
                  <option value="flag">Flag for Review</option>
                  <option value="feature">Feature Job</option>
                  <option value="unfeature">Remove Featured Status</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason (optional)
                </label>
                <input
                  type="text"
                  value={moderationAction.reason || ''}
                  onChange={(e) => setModerationAction(prev => ({ ...prev, reason: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Brief reason for this action"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Internal Notes (optional)
                </label>
                <textarea
                  rows={3}
                  value={moderationAction.notes || ''}
                  onChange={(e) => setModerationAction(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Internal notes for other administrators"
                />
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowModerationModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleModerationAction}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Apply Action
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobManagement;